import * as React from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { FilterBottomSheet, FilterBottomSheetOption } from "@/components/ui/filter-bottom-sheet";
import { useIsMobileFilterSheet } from "@/hooks/useIsMobileFilterSheet";

const DROPDOWN_MAX_HEIGHT_PX = 280;
const DROPDOWN_MIN_HEIGHT_PX = 120;
const DROPDOWN_GAP_PX = 4;

type FilterOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

function collectOptions(children: React.ReactNode, out: FilterOption[]): void {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      collectOptions((child.props as { children?: React.ReactNode }).children, out);
      return;
    }
    if (child.type !== "option") return;
    const props = child.props as React.OptionHTMLAttributes<HTMLOptionElement>;
    out.push({
      value: String(props.value ?? ""),
      label: String(props.children ?? ""),
      disabled: props.disabled,
    });
  });
}

function parseOptions(children: React.ReactNode): FilterOption[] {
  const options: FilterOption[] = [];
  collectOptions(children, options);
  return options;
}

export interface FilterSelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "children" | "size"> {
  children: React.ReactNode;
  /** Dropdown compacto para células de tabela (ex.: unidade em Entradas). */
  size?: "default" | "compact";
  mobileSheet?: boolean;
  /** Texto quando não há valor/opção selecionada (padrão: "Selecione…" ou "-" no compact). */
  placeholder?: string;
}

const FilterSelect = React.forwardRef<HTMLButtonElement, FilterSelectProps>(
  ({ className = "", children, value, defaultValue, onChange, disabled, id, "aria-label": ariaLabel, required: _required, name: _name, size = "default", placeholder, mobileSheet: mobileSheetEnabled = true, ...rest }, ref) => {
    const options = React.useMemo(() => parseOptions(children), [children]);
    const listOptions = React.useMemo(() => options.filter((o) => o.value !== ""), [options]);
    const [open, setOpen] = React.useState(false);
    const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties>({});
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const panelRef = React.useRef<HTMLDivElement>(null);
    const isCompact = size === "compact";
    const isMobileSheet = useIsMobileFilterSheet() && mobileSheetEnabled;

    React.useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement);

    const currentValue = value !== undefined ? String(value) : defaultValue !== undefined ? String(defaultValue) : "";
    const listboxId = id ? `${id}-listbox` : undefined;
    const sheetTitle = (ariaLabel && String(ariaLabel).trim()) || "Filtro";

    const updatePanelPosition = React.useCallback(() => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP_PX;
      const spaceAbove = rect.top - DROPDOWN_GAP_PX;
      const openUpward = spaceBelow < DROPDOWN_MIN_HEIGHT_PX && spaceAbove > spaceBelow;
      const availableSpace = openUpward ? spaceAbove : spaceBelow;
      const maxHeight = Math.min(
        DROPDOWN_MAX_HEIGHT_PX,
        Math.max(DROPDOWN_MIN_HEIGHT_PX, availableSpace - DROPDOWN_GAP_PX),
      );

      setPanelStyle({
        position: "fixed",
        top: openUpward ? Math.max(DROPDOWN_GAP_PX, rect.top - DROPDOWN_GAP_PX - maxHeight) : rect.bottom + DROPDOWN_GAP_PX,
        left: rect.left,
        width: rect.width,
        maxHeight,
        zIndex: 250,
      });
    }, []);

    React.useEffect(() => {
      if (!open || isMobileSheet) return;
      updatePanelPosition();
      const onScrollOrResize = () => updatePanelPosition();
      window.addEventListener("scroll", onScrollOrResize, true);
      window.addEventListener("resize", onScrollOrResize);
      return () => {
        window.removeEventListener("scroll", onScrollOrResize, true);
        window.removeEventListener("resize", onScrollOrResize);
      };
    }, [open, isMobileSheet, updatePanelPosition]);

    React.useEffect(() => {
      if (!open || isMobileSheet) return;
      const onPointerDown = (e: MouseEvent) => {
        const target = e.target as Node;
        if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
        setOpen(false);
      };
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", onPointerDown);
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.removeEventListener("mousedown", onPointerDown);
        document.removeEventListener("keydown", onKeyDown);
      };
    }, [open, isMobileSheet]);

    const selectValue = (nextValue: string) => {
      if (disabled) return;
      const opt = options.find((o) => o.value === nextValue);
      if (!opt || opt.disabled) return;
      setOpen(false);
      if (onChange) {
        const synthetic = {
          target: { value: nextValue },
          currentTarget: { value: nextValue },
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(synthetic);
      }
    };

    const triggerClassName = [
      isCompact
        ? "flex h-8 w-[4rem] min-w-[3.5rem] max-w-[4.5rem] shrink-0 items-center justify-between gap-0.5 rounded-lg border border-gray-200 bg-white px-1 py-0 text-xs text-gray-900 shadow-sm sm:h-9"
        : "flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm",
      disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const emptyLabel = placeholder ?? (isCompact ? "-" : "Selecione…");
    const matched = options.find((o) => o.value === currentValue);
    const displayLabel =
      currentValue !== "" && matched && matched.value !== "" ? matched.label : emptyLabel;

    const optionButtons = listOptions.map((opt) => {
      const isSelected = opt.value === currentValue;
      if (isMobileSheet) {
        return (
          <FilterBottomSheetOption
            key={opt.value === "" ? "__empty__" : opt.value}
            selected={isSelected}
            disabled={opt.disabled}
            onSelect={() => selectValue(opt.value)}
          >
            {opt.label}
          </FilterBottomSheetOption>
        );
      }
      return (
        <button
          key={opt.value === "" ? "__empty__" : opt.value}
          type="button"
          role="option"
          aria-selected={isSelected}
          disabled={opt.disabled}
          className={[
            "block w-full truncate whitespace-nowrap rounded-md text-left transition-colors",
            isCompact ? "px-2 py-1.5 text-center text-xs" : "px-3 py-2 text-sm",
            isSelected ? "bg-client-secondary font-medium text-gray-900" : "text-gray-900 hover:bg-gray-50",
            opt.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => selectValue(opt.value)}
        >
          {opt.label}
        </button>
      );
    });

    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          id={id}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          className={triggerClassName}
          onClick={() => {
            if (disabled) return;
            if (!open && !isMobileSheet) updatePanelPosition();
            setOpen((v) => !v);
          }}
          {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          <span
            className={
              isCompact
                ? "min-w-0 flex-1 truncate whitespace-nowrap text-center text-[0.65rem] leading-none sm:text-xs"
                : "min-w-0 flex-1 truncate whitespace-nowrap text-left"
            }
          >
            {displayLabel}
          </span>
          {open ? (
            <ChevronUp className={isCompact ? "h-3 w-3 shrink-0 text-gray-500" : "h-4 w-4 shrink-0 text-gray-500"} aria-hidden />
          ) : (
            <ChevronDown className={isCompact ? "h-3 w-3 shrink-0 text-gray-500" : "h-4 w-4 shrink-0 text-gray-500"} aria-hidden />
          )}
        </button>

        {isMobileSheet ? (
          <FilterBottomSheet
            open={open}
            title={sheetTitle}
            listboxId={listboxId}
            onClose={() => setOpen(false)}
          >
            {optionButtons}
          </FilterBottomSheet>
        ) : open && typeof document !== "undefined" ? (
          createPortal(
            <div
              ref={panelRef}
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel}
              style={panelStyle}
              className="overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white p-1.5 shadow-md"
              onWheel={(e) => e.stopPropagation()}
            >
              {optionButtons}
            </div>,
            document.body,
          )
        ) : null}
      </>
    );
  },
);
FilterSelect.displayName = "FilterSelect";

export { FilterSelect };
